import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Radio, ArrowRight, MessageSquare, ArrowLeft, DollarSign } from 'lucide-react';
import '../styles/agentPage.css';
import '../styles/channelAgent.css';

function ChannelAgent() {
  const { user, isAuthenticated, refreshToken } = useAuth();
  const navigate = useNavigate();
  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [channelDataSufficient, setChannelDataSufficient] = useState(false);
  const [currentAudience, setCurrentAudience] = useState(null);
  const [showAudienceSelector, setShowAudienceSelector] = useState(false);
  const [availableAudiences, setAvailableAudiences] = useState([]);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const chatWindowRef = useRef(null);
  
  const formatMessage = useCallback((content) => {
    return content
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/- (.*?)(?:\n|$)/g, '<li>$1</li>')
    .replace(/<li>/g, '<ul><li>').replace(/<\/li>\n/g, '</li></ul>')
    .replace(/(\w+)\[(https?:\/\/[^\]]+)\]/g, '<a href="$2" target="_blank">$1</a>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  }, []);

  // Function to fetch available audiences from Airtable using GET
  const fetchAvailableAudiences = async () => {
    setAudienceLoading(true);
    try {
      const response = await axios.get(
        'https://n8n.iniai.cloud/webhook/get-prob-audiences',
        {
          params: {
            userId: user?.id || '',
            userName: user?.name || ''
          },
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        setAvailableAudiences(response.data);
      }
      setShowAudienceSelector(true);
    } catch (error) {
      console.error('Error fetching audiences:', error);
    } finally {
      setAudienceLoading(false);
    }
  };
  
  // Function to select an audience
  const handleAudienceSelect = (audience) => {
    // Format audience data as needed
    const selectedAudience = {
      name: audience['Audience Name'],
      size: audience['Audience Size'].toLocaleString(),
      market: audience['Audience Market']
    };
    
    // Save to session storage
    sessionStorage.setItem('currentAudience', JSON.stringify(selectedAudience));
    setCurrentAudience(selectedAudience);
    setShowAudienceSelector(false);
    setChat([]); // Clear chat history for the new audience
  };
  
  // Function to change audience
  const handleChangeAudience = () => {
    // Clear chat history since it's specific to the previous audience
    setChat([]);
    // Show audience selector
    fetchAvailableAudiences();
  };

  // Get audience from session storage
  useEffect(() => {
    const storedAudience = sessionStorage.getItem('currentAudience');
    if (storedAudience) {
      setCurrentAudience(JSON.parse(storedAudience));
    } else {
      // Instead of redirecting, fetch available audiences
      fetchAvailableAudiences();
    }
  }, []);

  const getWelcomeMessage = useCallback(async () => {
    if (!currentAudience) return;

    try {
      await refreshToken();
      const result = await axios.post(
        'https://n8n.iniai.cloud/webhook/chanel-channel-agent',
        {
          type: 'welcome',
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          audience: currentAudience,
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`
        },
        {
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            Authorization: `Bearer ${user?.token || ''}`
          }
        }
      );

      if (Array.isArray(result.data) && result.data.length > 0) {
        const response = result.data[0].output || result.data[0].response;
        if (response) {
          setChat([{ 
            type: 'agent', 
            content: formatMessage(response)
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
      setChat([{
        type: 'agent',
        content: `Welcome to the Channel Pricing Agent. I'll help you gather pricing data for channels to reach the "${currentAudience?.name}" audience in the ${currentAudience?.market} market. What channels are you interested in?`
      }]);
    }
  }, [user, isAuthenticated, refreshToken, formatMessage, currentAudience]);

  useEffect(() => {
    if (isAuthenticated && user && chat.length === 0 && currentAudience) {
      getWelcomeMessage();
    }
  }, [isAuthenticated, user, chat.length, getWelcomeMessage, currentAudience]);

  useEffect(() => {
    if (chatWindowRef.current) {
      requestAnimationFrame(() => {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      });
    }
  }, [chat]);

  // Check for channel data confirmation in the agent's response
  useEffect(() => {
    if (chat.length > 1) {
      const lastMessage = chat[chat.length - 1];
      if (lastMessage.type === 'agent') {
        // Look for channel data confirmation in the message
        const content = lastMessage.content.toLowerCase();
        if (content.includes('data saved') || 
            content.includes('channel pricing updated') || 
            content.includes('added to database') ||
            content.includes('pricing details saved')) {
          setChannelDataSufficient(true);
        }
        
        // Check if we have at least 3 channels added to consider the data sufficient
        // This is a simplified check - in a real app you'd have a better way to track this
        let channelCount = 0;
        chat.forEach(msg => {
          if (msg.type === 'agent' && 
             (msg.content.toLowerCase().includes('added to database') || 
              msg.content.toLowerCase().includes('pricing details saved'))) {
            channelCount++;
          }
        });
        
        if (channelCount >= 2) {
          setChannelDataSufficient(true);
        }
      }
    }
  }, [chat]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
  
    const userMessage = { type: 'user', content: query };
    setChat(prevChat => [...prevChat, userMessage]);
    const currentQuery = query;
    setQuery('');
    setLoading(true);
  
    try {
      await refreshToken();
      const result = await axios.post(
        'https://n8n.iniai.cloud/webhook/chanel-channel-agent',
        {
          type: 'query',
          query: currentQuery,
          audience: currentAudience,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          }
        }
      );
  
      if (Array.isArray(result.data) && result.data.length > 0) {
        const formattedContent = formatMessage(result.data[0].response || result.data[0].output);
        setChat(prevChat => [...prevChat, { type: 'agent', content: formattedContent }]);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while processing your request.';
      setChat(prevChat => [...prevChat, { type: 'agent', content: errorMessage }]);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToAffinity = () => {
    navigate('/agent/affinity');
  };

  const handleBackToAudience = () => {
    navigate('/agent/audience');
  };

  const handleSkipToPlanner = () => {
    navigate('/reachcurve/planner');
  };

  // Show audience selector if no audience is selected
  if (showAudienceSelector) {
    return (
      <div className="agent-container">
        <div className="agent-header">
          <div className="agent-title">
            <DollarSign className="agent-icon" />
            <h1>Chanel: Channel Pricing Agent</h1>
          </div>
          <p className="agent-description">
            Chanel helps you gather channel pricing and performance data for your campaigns.
          </p>
        </div>
        
        <div className="audience-selector">
          <h2>Select an Audience</h2>
          {audienceLoading ? (
            <div className="loading-indicator">Loading audiences...</div>
          ) : availableAudiences.length === 0 ? (
            <div className="no-audiences">
              <p>No audiences found. Please create an audience first.</p>
              <button onClick={() => navigate('/agent/audience')} className="create-audience-btn">
                Create Audience
              </button>
            </div>
          ) : (
            <div className="audience-list">
              {availableAudiences.map((audience) => (
                <div 
                  key={audience.id} 
                  className="audience-card"
                  onClick={() => handleAudienceSelect(audience)}
                >
                  <h3>{audience['Audience Name']}</h3>
                  <div className="audience-details">
                    <span className="audience-size">{audience['Audience Size'].toLocaleString()} people</span>
                    <span className="audience-market">{audience['Audience Market']}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show the regular channel agent UI when an audience is selected
  return (
    <div className="agent-container">
      <div className="agent-header">
        <div className="agent-title">
          <DollarSign className="agent-icon" />
          <h1>Chanel: Channel Pricing Agent</h1>
        </div>
        <p className="agent-description">
          Chanel helps you gather channel pricing and performance data for your campaigns.
        </p>
      </div>

      <div className="audience-reference">
        <div className="audience-header">
          <h3>Working with audience:</h3>
          <button 
            className="change-audience-btn" 
            onClick={handleChangeAudience}
            title="Select a different audience"
          >
            Change
          </button>
        </div>
        <div className="audience-badge">
          <span className="audience-name">{currentAudience?.name}</span>
          <span className="audience-size">{currentAudience?.size}</span>
          <span className="audience-market">{currentAudience?.market}</span>
        </div>
      </div>

      {channelDataSufficient && (
        <div className="channel-data-sufficient">
          <div className="sufficient-notice">
            <Radio className="check-icon" />
            <h3>Channel Data Collected</h3>
            <p>You have gathered enough channel pricing data for your campaign.</p>
          </div>
          <div className="next-steps">
            <h4>What would you like to do next?</h4>
            <div className="option-buttons">
              <button className="option-button" onClick={handleContinueToAffinity}>
                <MessageSquare className="option-icon" />
                <span>Channel Affinities</span>
                <ArrowRight className="arrow-icon" />
              </button>
              <button className="option-button skip" onClick={handleSkipToPlanner}>
                <Radio className="option-icon" />
                <span>Skip to Reach Curve</span>
                <ArrowRight className="arrow-icon" />
              </button>
              <button className="option-button back" onClick={handleBackToAudience}>
                <ArrowLeft className="option-icon" />
                <span>Back to Audience</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chat-window" ref={chatWindowRef}>
        {chat.map((message, index) => (
          <div
            key={index}
            className={`message-bubble ${message.type === 'user' ? 'user' : 'agent'}`}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: message.content }} 
              className="message-content"
            />
          </div>
        ))}

        {loading && (
          <div className="message-bubble agent">
            <p className="loading-text">
              Chanel is researching channel pricing
              <span className="dots">...</span>
            </p>
          </div>
        )}
      </div>

      <form className="chat-input" onSubmit={handleSubmit}>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask about specific channels (e.g., What's the CPM for Instagram in the UK market?)..."
          rows="3"
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Processing...' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default ChannelAgent;